import {createSlice, current, PayloadAction} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {
  SOURCE_AMBIENT_WEATHER,
  SOURCE_DAVIS,
  SOURCE_ECOWITT,
  SOURCE_MY_ACURITE,
  SOURCE_NETATMO_WEATHER,
  SOURCE_SYNOP,
  SOURCE_WEATHERFLOW,
  SOURCE_WUNDERGROUND,
} from '../../api';
import {
  DataGranularity,
  StationSource,
  WeatherStationFromList,
} from '../../api/types';
import {Dates, Time} from '../../utils';
import {ForecastModelConfig} from '../../utils/Forecast/types';

export type GenericStationComparisonConfig = {
  type: string;
  params?: {
    station: WeatherStationFromList;
  };
};

type ChartConfig = {
  scale: Time.Scale;
  granularity: DataGranularity;
  comparison: GenericStationComparisonConfig | null;
};

export type DashboardStationConfig = {
  order: string[];
  hidden: string[];
};

type WeatherState = {
  // Save favorite stations IDs
  favoriteStationIds: number[];
  stationList: WeatherStationFromList[];
  stationSort: number[];
  currentStation: number | null;
  stationMapFilter: {
    source: StationSource[];
    metric: 'T' | 'FXY' | 'RR_1H';
  };
  chartConfig: ChartConfig;
  // Weather forecast
  forecastModels: Record<number, ForecastModelConfig[]>;
  selectedForecastModel: Record<number, string>;
  // Dashboard customization per station
  dashboardConfig: Record<number, DashboardStationConfig>;
};

export const initialState: WeatherState = {
  favoriteStationIds: [],
  stationList: [],
  stationSort: [],
  currentStation: null,
  stationMapFilter: {
    source: [
      SOURCE_SYNOP,
      SOURCE_NETATMO_WEATHER,
      SOURCE_DAVIS,
      SOURCE_WUNDERGROUND,
      SOURCE_AMBIENT_WEATHER,
      SOURCE_ECOWITT,
      SOURCE_MY_ACURITE,
      SOURCE_WEATHERFLOW,
    ],
    metric: 'T',
  },
  chartConfig: {
    scale: Dates.DAY,
    granularity: 'H',
    comparison: null,
  },
  // Weather forecasts
  forecastModels: {},
  selectedForecastModel: {},
  // Dashboard customization
  dashboardConfig: {},
};

export const weatherSlice = createSlice({
  name: 'weather',
  initialState,
  reducers: {
    /**
     * Drop persisted stations nothing pins any more.
     *
     * `liveStationIds` is the complete list the server just returned for this
     * user (`/me` `stations`: every favorite + every linked station). When it
     * is given, a station pinned only by its persisted `link` must also be in
     * it: a linked station the server no longer returns was deleted or
     * unlinked, and its stale `link` would otherwise keep it forever (#285).
     * Without it (anonymous launch, no /me yet) every linked station stays,
     * since nothing proves a link is stale.
     */
    cleanUpStationList: (
      state,
      action: PayloadAction<{liveStationIds: number[]} | undefined>,
    ) => {
      const liveStationIds = action.payload?.liveStationIds;
      const isLinkLive = (s: WeatherStationFromList) =>
        !!s.link && (!liveStationIds || liveStationIds.includes(s.id));
      const pinnedIds = [
        ...new Set(
          // keep favorite stations
          state.favoriteStationIds
            // + linked stations (the server still returns)
            .concat(state.stationList.filter(isLinkLive).map((s) => s.id))
            // + currently displayed station (if any)
            .concat(state.currentStation || []),
        ),
      ];

      // Keep only favorite stations + currentStation
      state.stationList = state.stationList.filter((s) =>
        pinnedIds.includes(s.id),
      );
    },
    setFavoriteStationList: (
      state,
      {payload: stationList}: {payload: WeatherStationFromList[]},
    ) => {
      state.favoriteStationIds = stationList.map((station) => station.id);
    },
    removeFavoriteStation: (state, {payload: stationId}: {payload: number}) => {
      state.favoriteStationIds = state.favoriteStationIds.filter(
        (id) => id !== stationId,
      );
    },
    addFavoriteStation: (state, {payload: stationId}: {payload: number}) => {
      state.favoriteStationIds.push(stationId);
    },
    addStation: (
      state,
      {payload: station}: {payload: WeatherStationFromList},
    ) => {
      const existingIndex = state.stationList.findIndex(
        (s) => s.id === station.id,
      );
      // No-op churn guard (#41): an identical station-detail poll must not
      // replace the stationList reference, otherwise every memoized selector
      // recomputes and the dashboard cards re-serialize their charts into the
      // Reanimated worklet runtime (SMARTMIXIN-IOS-61..6B). `current()`
      // materializes the Immer draft so react-fast-compare sees a plain object.
      // The stationSort check keeps the self-heal below reachable for persisted
      // states where the station never made it into the sort list.
      if (
        existingIndex !== -1 &&
        state.stationSort.includes(station.id) &&
        isEqual(current(state.stationList[existingIndex]), station)
      ) {
        return;
      }
      state.stationList = state.stationList
        .filter((s) => s.id !== station.id)
        .concat([station]);
      if (!state.stationSort.includes(station.id)) {
        // Add station id if not already in the sorted list
        state.stationSort = state.stationSort.concat([station.id]);
      }
    },
    setStationList: (
      state,
      {payload: stationList}: {payload: WeatherStationFromList[]},
    ) => {
      const newIds = stationList.map((s) => s.id);
      state.stationList = state.stationList
        .filter((s) => !newIds.includes(s.id))
        .concat(stationList);
      if (state.stationSort.length !== stationList.length) {
        // Add station ids that are not yet in the sorted list
        state.stationSort = state.stationSort.concat(
          stationList
            .filter((s) => !state.stationSort.includes(s.id))
            .map((s) => s.id),
        );
      }
    },
    removeStation: (state, {payload: stationId}: {payload: number}) => {
      // Remove station from lists
      state.stationList = state.stationList.filter((s) => s.id !== stationId);
      // Remove from the sorted list
      state.stationSort = state.stationSort.filter((id) => id !== stationId);
      // reset currentStation if this was the station being removed
      if (state.currentStation === stationId) {
        state.currentStation = null;
      }
      // Remove from favorite stations if it was a favorite
      state.favoriteStationIds = state.favoriteStationIds.filter(
        (id) => id !== stationId,
      );
      // Remove from the forecast models if it was a forecasted station
      delete state.forecastModels[stationId];
      delete state.selectedForecastModel[stationId];
      // Remove dashboard customization
      if (state.dashboardConfig) {
        delete state.dashboardConfig[stationId];
      }
    },
    setCurrentPublicStation: (
      state,
      {payload: stationId}: {payload: number},
    ) => {
      state.currentStation = stationId;
    },
    setStationMapSource: (
      state,
      {payload: choices}: {payload: StationSource[]},
    ) => {
      state.stationMapFilter.source = choices;
    },
    setMapMetric: (
      state,
      {payload: value}: {payload: 'T' | 'FXY' | 'RR_1H'},
    ) => {
      state.stationMapFilter.metric = value;
    },
    // History
    setWeatherChartComparison: (
      state,
      {
        payload: comparisonConfig,
      }: {payload: GenericStationComparisonConfig | null},
    ) => {
      state.chartConfig = {
        ...state.chartConfig,
        comparison: comparisonConfig,
      };
    },
    setWeatherChartScale: (state, {payload: scale}: {payload: Time.Scale}) => {
      state.chartConfig = {...state.chartConfig, scale};
    },
    setWeatherChartGranularity: (
      state,
      {payload: granularity}: {payload: DataGranularity},
    ) => {
      state.chartConfig = {...state.chartConfig, granularity};
    },
    setStationSort: (state, {payload: value}: {payload: number[]}) => {
      state.stationSort = value;
    },
    // Dashboard customization reducers
    setDashboardOrder: (
      state,
      {
        payload: {stationId, order},
      }: {payload: {stationId: number; order: string[]}},
    ) => {
      state.dashboardConfig ??= {};
      if (!state.dashboardConfig[stationId]) {
        state.dashboardConfig[stationId] = {order: [], hidden: []};
      }
      state.dashboardConfig[stationId].order = order;
    },
    toggleDashboardComponentVisibility: (
      state,
      {
        payload: {stationId, componentId},
      }: {payload: {stationId: number; componentId: string}},
    ) => {
      state.dashboardConfig ??= {};
      if (!state.dashboardConfig[stationId]) {
        state.dashboardConfig[stationId] = {order: [], hidden: []};
      }
      const config = state.dashboardConfig[stationId];
      if (config.hidden.includes(componentId)) {
        config.hidden = config.hidden.filter((id) => id !== componentId);
      } else {
        config.hidden.push(componentId);
      }
    },
    // Whole-map hydration from the server. Stores whatever the server returns
    // (string keys on the wire); JS key coercion keeps reads via numeric ids
    // working. `null` resets to an empty map.
    setDashboardConfig: (
      state,
      {payload}: {payload: Record<string, DashboardStationConfig> | null},
    ) => {
      state.dashboardConfig = payload ?? {};
    },
    // Weather Forecast reducers
    setWeatherForecastModels: (
      state,
      {
        payload: {stationId, forecastModels},
      }: {
        payload: {
          stationId: number;
          forecastModels: ForecastModelConfig[];
        };
      },
    ) => {
      const newForecastModels = {} as Record<number, ForecastModelConfig[]>;
      newForecastModels[stationId] = forecastModels;
      state.forecastModels = {...state.forecastModels, ...newForecastModels};
    },
    setWeatherSelectedForecastModel: (
      state,
      {
        payload: {stationId, forecastModel},
      }: {payload: {stationId: number; forecastModel: string}},
    ) => {
      const newSelectedForecastModel = {} as Record<number, string>;
      newSelectedForecastModel[stationId] = forecastModel;
      state.selectedForecastModel = {
        ...state.selectedForecastModel,
        ...newSelectedForecastModel,
      };
    },
    /**
     * Stop overriding this station, so it follows the profile preference again.
     *
     * `delete` rather than assigning undefined: `resolveSelectedForecastModel`
     * branches on `id in selectedForecastModelKeyDict`, and an own key holding
     * undefined would still read as an override and resolve to no model at all.
     */
    clearWeatherSelectedForecastModel: (
      state,
      {payload: {stationId}}: {payload: {stationId: number}},
    ) => {
      delete state.selectedForecastModel[stationId];
    },
  },
});

export const {
  cleanUpStationList,
  setFavoriteStationList,
  removeFavoriteStation,
  addFavoriteStation,
  addStation,
  setStationList,
  removeStation,
  setCurrentPublicStation,
  setStationMapSource,
  setMapMetric,
  setWeatherChartComparison,
  setWeatherChartScale,
  setWeatherChartGranularity,
  setStationSort,
  setDashboardOrder,
  toggleDashboardComponentVisibility,
  setDashboardConfig,
  setWeatherForecastModels,
  setWeatherSelectedForecastModel,
  clearWeatherSelectedForecastModel,
} = weatherSlice.actions;

export default weatherSlice.reducer;
